import React, { useRef } from 'react';
import { cloneDeep, debounce, parseInt, toString } from 'lodash';
import { ProductRates } from '@upstart/harmony/lib/legos/penTypes';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { isBackendProduct } from '@upstart/harmony/lib/pen-utils';
import { fmtPriceSimple, fmtPrice } from '@upstart/harmony/lib/formatter';
import {
  DeskingProduct,
  getCoverageSelectOptions,
  getDefaultCoverageOption,
  getDefaultTermOption,
  getTermSelectOptions,
} from 'util/protectionPlans';
import { FinanceType } from '@upstart/harmony/lib/constants';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import Input from '../Input';
import { TermOptions } from './type';

type Products = {
  [key: string]: DeskingProduct;
};

type PriceOverrides = { [key: string]: number };

interface Props {
  fetching?: boolean;
  productList?: DeskingProduct[];
  products?: Products;
  rates?: ProductRates;
  termOptions?: TermOptions;
  protectionPlanProductPriceOverrides?: PriceOverrides;
  minMaxDebounceInEffect?: boolean;
  productIdBeingEdited?: string;
  toggleProductSelect: (id: string) => void;
  toggleInCapCostFlag: (id: string, isOnePayLease: boolean) => void;
  setProducts: (products: Products) => void;
  setProductIdBeingEdited: (id?: string) => void;
  setProtectionPlanProductOverrides: (newOverrides: PriceOverrides) => void;
  setMinMaxDebounceInEffect: (inEffect: boolean) => void;
  productBackendCalculationVersion?: number;
  displayBackendBadge?: boolean;
  disabled?: boolean;
  offer?: DeskingOffer;
  isOnePayLease: boolean;
}

// Given a price, minPrice and maxPrice - if price exceeds the bounds of minPrice and maxPrice,
// return either minPrice or maxPrice. If price is within the bounds, just return the price (noop),
const restrictByMinAndMaxPrice = (
  price: number,
  minPrice: number | undefined,
  maxPrice: number | undefined,
) => {
  // If there's a max price constraint in effect, it's value will be different than -1
  // If maxPrice is -1, it means there are no limits on maxPrice
  if (maxPrice !== -1 && maxPrice !== undefined && price > maxPrice) {
    return maxPrice;
  }

  // Same logic applies to minPrice (the -1 behavior)
  if (minPrice !== -1 && minPrice !== undefined && price < minPrice) {
    return minPrice;
  }

  // Do nothing
  return price;
};

const formatTermLabel = (label?: string) => {
  if (!label) return label;

  const labelPattern = /(\d+)\s*mo\s*(\d+)\s*miles/;

  const matches = label.match(labelPattern);
  if (!matches) return label;
  const months = matches[1];
  const miles = parseInt(matches[2], 10);

  let formattedLabel = `${months} mo`;
  if (miles !== 0 && miles !== 999999) {
    formattedLabel += ` ${Math.floor(miles / 1000)}k miles`;
  }

  return formattedLabel;
};

function ProtectionPlansList(props: Props) {
  // Given a change in the override value for a product, apply min and max price limits
  const changeOverride = (
    value: number | string,
    id: string,
    localOverrides: PriceOverrides,
    localProducts: Products,
  ) => {
    const overrides = cloneDeep(localOverrides);
    if (parseInt(toString(value), 10) >= 0) {
      const restrictedByMinAndMaxPrice = restrictByMinAndMaxPrice(
        parseCurrencyTruncated(value),
        localProducts[id].minRetailPrice,
        localProducts[id].maxRetailPrice,
      );
      overrides[id] = restrictedByMinAndMaxPrice;
    } else {
      delete overrides[id];
    }
    props.setMinMaxDebounceInEffect(false);
    props.setProductIdBeingEdited();
  };

  const changeOverrideDebounced = useRef(debounce(changeOverride, 1000));

  return (
    <div>
      <div className="protection-plans-modal-products-header-row">
        <div className="protection-plans-modal-header-check">{''}</div>
        <div className="protection-plans-modal-product-header-name">Name</div>
        <div className="protection-plans-modal-product-header-rates">Rates</div>
        <div className="protection-plans-modal-product-header-coverage">
          Coverage level
        </div>
        {props.offer?.data?.financeType === FinanceType.LEASE && (
          <div className="protection-plans-modal-product-capcost">
            In Cap Cost
          </div>
        )}
        <div className="protection-plans-modal-product-header-price">Price</div>
        <div className="protection-plans-modal-product-header-price">
          Min/Max
        </div>
        <div className="protection-plans-modal-product-header-price-override">
          Override
        </div>
      </div>
      <div className="protection-plans-modal-products">
        {!props.fetching && props.productList?.length === 0 && (
          <div className="text-center">No products available</div>
        )}
        {props.productList
          ?.filter(product => {
            // If Pen product, filter if in rates, otherwise display product
            if (product.source === 'provider') {
              return props.rates?.[product.id];
            }
            return true;
          })
          .map(product => {
            const termOptions = props.termOptions?.[product.id] ?? [];
            const selectedTermOption = getDefaultTermOption(
              product,
              termOptions,
            );
            const selectedCoverageOption = getDefaultCoverageOption({
              product,
              termOptions,
            });
            const termSelectOptions = getTermSelectOptions(
              termOptions,
              selectedCoverageOption?.value?.coverageName,
            );
            const coverageNameOptions = getCoverageSelectOptions({
              termOptions,
              product,
            });

            return (
              <div key={product.id} className="protection-plans-modal-product">
                <div className="protection-plans-modal-check-container">
                  <Input
                    type="checkbox"
                    checked={product.selected || false}
                    className="protection-plans-modal-check"
                    onChange={() => props.toggleProductSelect(product.id)}
                    disabled={!!props.disabled}
                  />
                </div>
                <div className="protection-plans-modal-product-name">
                  {product.displayName}
                  {props.displayBackendBadge &&
                    isBackendProduct(
                      product,
                      props.productBackendCalculationVersion,
                    ) && (
                      <span className="protection-plans-modal-product-backend-tag">
                        Backend
                      </span>
                    )}
                </div>
                <div className="protection-plans-rates-container">
                  {!!product.term && (
                    <select
                      className="protection-plans-rates"
                      value={selectedTermOption?.label}
                      onChange={e => {
                        const option =
                          termOptions.find(
                            o =>
                              o.renderValue === e.target.value &&
                              o.coverageName ===
                                selectedCoverageOption?.value.coverageName,
                          ) ||
                          termOptions.find(
                            o => o.renderValue === e.target.value,
                          );
                        const productMap = cloneDeep(props.products ?? {});
                        productMap[product.id].term = option?.term;
                        productMap[product.id].miles = option?.miles;
                        productMap[product.id].monthlyPayment =
                          option?.deductible?.monthlyPayment;
                        productMap[product.id].retailPrice =
                          option?.deductible?.retailPrice;
                        productMap[product.id].minRetailPrice =
                          option?.deductible?.minRetailPrice;
                        productMap[product.id].maxRetailPrice =
                          option?.deductible?.maxRetailPrice;
                        productMap[product.id].bundlePrices =
                          option?.bundlePrices;
                        productMap[product.id].coverageName =
                          option?.coverageName;

                        props.setProducts(productMap);
                        // If there is an existing override, check if that is still valid after the term/miles change
                        if (
                          props.protectionPlanProductPriceOverrides?.[
                            product.id
                          ]
                        ) {
                          changeOverride(
                            props.protectionPlanProductPriceOverrides[
                              product.id
                            ],
                            product.id,
                            props.protectionPlanProductPriceOverrides,
                            productMap,
                          );
                        }
                      }}
                    >
                      {termSelectOptions.map(o => (
                        <option value={o.value}>
                          {formatTermLabel(o.label)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="protection-plans-coverage-level-container">
                  {!!product.term && (
                    <select
                      className="protection-plans-coverages"
                      value={selectedCoverageOption?.label}
                      onChange={e => {
                        const option =
                          termOptions.find(
                            o =>
                              o.coverageName === e.target.value &&
                              o.renderValue ===
                                selectedTermOption?.value.renderValue,
                          ) ||
                          termOptions.find(
                            o => o.coverageName === e.target.value,
                          );
                        const productMap = cloneDeep(props.products ?? {});
                        productMap[product.id].term = option?.term;
                        productMap[product.id].miles = option?.miles;
                        productMap[product.id].monthlyPayment =
                          option?.deductible?.monthlyPayment;
                        productMap[product.id].retailPrice =
                          option?.deductible?.retailPrice;
                        productMap[product.id].minRetailPrice =
                          option?.deductible?.minRetailPrice;
                        productMap[product.id].maxRetailPrice =
                          option?.deductible?.maxRetailPrice;
                        productMap[product.id].bundlePrices =
                          option?.bundlePrices;
                        productMap[product.id].coverageName =
                          option?.coverageName;

                        props.setProducts(productMap);
                        // If there is an existing override, check if that is still valid after the term/miles change
                        if (
                          props.protectionPlanProductPriceOverrides?.[
                            product.id
                          ]
                        ) {
                          changeOverride(
                            props.protectionPlanProductPriceOverrides[
                              product.id
                            ],
                            product.id,
                            props.protectionPlanProductPriceOverrides,
                            productMap,
                          );
                        }
                      }}
                    >
                      {coverageNameOptions.map(o => (
                        <option value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                {props.offer?.data?.financeType === FinanceType.LEASE && (
                  <div className="protection-plans-modal-check-container">
                    <Input
                      type="checkbox"
                      checked={
                        (props.isOnePayLease
                          ? product.inCapCostForOnePayLease
                          : product.inCapCostForLease) || false
                      }
                      className="protection-plans-modal-check"
                      onChange={() =>
                        props.toggleInCapCostFlag(
                          product.id,
                          props.isOnePayLease,
                        )
                      }
                      disabled={!product.selected || !!props.disabled}
                    />
                  </div>
                )}
                <div className="protection-plans-modal-product-price">
                  {fmtPrice(product.finalPrice || product.retailPrice || 0)}
                </div>
                <div className="protection-plans-modal-product-min-max">
                  {product.minRetailPrice === -1 ||
                  product.minRetailPrice === undefined
                    ? '-'
                    : fmtPriceSimple(product.minRetailPrice)}{' '}
                  /{' '}
                  {product.maxRetailPrice === -1 ||
                  product.maxRetailPrice === undefined
                    ? '-'
                    : fmtPriceSimple(product.maxRetailPrice)}
                </div>
                <div className="protection-plans-modal-product-price-override">
                  <input
                    type="number"
                    onChange={e => {
                      // The flag is used to signal that we are applying min and max prices restriction, and disabling user to save
                      props.setProductIdBeingEdited(product.id);
                      props.setMinMaxDebounceInEffect(true);

                      const updatedOverrides = {
                        ...(props.protectionPlanProductPriceOverrides ?? {}),
                      };
                      const parsedValue = parseFloat(
                        parseFloat(e.target.value).toFixed(2),
                      );
                      if (Number.isNaN(parsedValue)) {
                        delete updatedOverrides[product.id];
                      } else {
                        updatedOverrides[product.id] = parsedValue;
                      }
                      props.setProtectionPlanProductOverrides(updatedOverrides);
                      changeOverrideDebounced.current(
                        e.target.value,
                        product.id,
                        props.protectionPlanProductPriceOverrides ?? {},
                        props.products ?? {},
                      );
                    }}
                    value={
                      props.protectionPlanProductPriceOverrides?.[product.id] ??
                      ''
                    }
                    disabled={
                      !!props.disabled ||
                      (props.minMaxDebounceInEffect &&
                        props.productIdBeingEdited !== product.id)
                    }
                  />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default ProtectionPlansList;
